import { useMutation } from '@apollo/client'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import {
  CheckboxInputStyle,
  UncontrolledRadioInput,
} from '@src/components/Inputs/CheckboxInput/CheckboxInput'
import { ErrorPage } from '@src/components/Errors/ErrorPage'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { PaymentCard } from '@src/components/PaymentCard/PaymentCard'
import { SetDefaultCardDocument } from '@src/components/PaymentCard/mutations/__generated__/SetDefaultCard.graphql-interface'
import {
  Card,
  usePaymentCardsQuery,
} from '@src/hooks/sharedQueries/usePaymentCards/usePaymentCards'

export const PaymentCards: React.FC = () => {
  const { data, loading, error, refetch } = usePaymentCardsQuery()
  const { t } = useTranslation('paymentDetails')
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)

  const [setDefaultCard] = useMutation(SetDefaultCardDocument, {
    onCompleted: () => {
      toast.success(t('default_card_updated'))
      void refetch()
    },
    onError: () => {
      setSelectedCardId(null)
    },
  })

  if (loading) return <LoadingSpinner />
  if (!data || error) return <ErrorPage logError={error} />

  const cards = data.customerDetails.cards.cards || []

  return (
    <>
      <CardListTitle>{t('manage_default_card')}</CardListTitle>
      <CardListContainer>
        <PaymentCardsList>
          {cards.map((card: Card) => {
            const isSelected =
              selectedCardId !== null
                ? card.id === selectedCardId
                : card.isDefault

            return (
              <CardRow key={card.id}>
                <UncontrolledRadioInput
                  value={card.id}
                  disabled={card.isExpired}
                  label={<PaymentCard card={card} refetchList={refetch} />}
                  checked={isSelected && !card.isExpired}
                  style={CheckboxInputStyle.TICK}
                  onChange={() => {
                    setSelectedCardId(card.id)
                    void setDefaultCard({ variables: { cardId: card.id } })
                  }}
                />
              </CardRow>
            )
          })}
        </PaymentCardsList>
      </CardListContainer>
    </>
  )
}

const CardListContainer = styled.div`
  background: #f6f6f9;
  border-radius: 8px;
  padding: 0 16px 16px 16px;
  overflow: hidden;
`

const CardListTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 16px 0;
`

const PaymentCardsList = styled.div`
  padding: 0;
`

const CardRow = styled.div`
  margin-top: 18px;

  /* Keep delete button usable even when radio is disabled (expired cards) */
  label button {
    visibility: visible;
    color: #000000;
    cursor: pointer;
    opacity: 1;
  }
`
